import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || JSON.stringify(item)).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function loadProfile(session) {
  if (!session?.access_token) return null;
  const { data } = await api.get("/auth/session", {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  return data;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    const syncSession = async (nextSession) => {
      if (!active) return;
      setSession(nextSession);
      if (!nextSession) {
        setUser(null);
        setChecking(false);
        return;
      }
      try {
        const profile = await loadProfile(nextSession);
        if (active) setUser(profile);
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setChecking(false);
      }
    };

    supabase.auth.getSession().then(({ data, error }) => syncSession(error ? null : data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      window.setTimeout(() => syncSession(nextSession), 0);
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw new Error(formatApiErrorDetail(error.message));
    const profile = await loadProfile(data.session);
    setSession(data.session);
    setUser(profile);
    return profile;
  };

  const register = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: { name: name.trim() },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw new Error(formatApiErrorDetail(error.message));
    setSession(data.session);
    setUser(data.session ? await loadProfile(data.session) : null);
    return data;
  };

  const requestPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw new Error(formatApiErrorDetail(error.message));
  };

  const updatePassword = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(formatApiErrorDetail(error.message));
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(formatApiErrorDetail(error.message));
    setSession(null);
    setUser(null);
  };

  const value = useMemo(() => ({ session, user, checking, login, register, requestPasswordReset, updatePassword, logout }), [session, user, checking]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
